import type { ValidatedAnalyticsEvent } from "@/lib/analytics-schema";

/**
 * Shared event logging + field mapping for client and server.
 *
 * Both sides format log lines with `formatEventLog` and map payload fields to
 * database columns with `toConversionRow`, so naming conventions stay in one
 * place: camelCase in the payload, snake_case in the database, and a single
 * `[event] <page> <name> "<label>" key=value …` log line everywhere.
 */

export const LOG_PREFIX = "[event]";

/** payload field -> log key (kept stable, they end up in log searches). */
export const LOG_KEYS = {
  ctaType: "cta",
  target: "target",
  formId: "form",
  formStatus: "status",
  detail: "detail",
  clickY: "clickY",
  scrollY: "scrollY",
  documentY: "documentY",
  sectionVersion: "section",
} as const;

/** payload field -> database column in public.conversion_events. */
export const COLUMN_NAMES = {
  name: "name",
  label: "label",
  page: "page",
  target: "target",
  ctaType: "cta_type",
  formId: "form_id",
  formStatus: "form_status",
  detail: "detail",
  utm: "utm",
} as const;

type Extra = { documentY?: number | null | undefined };

/** `key=value` pairs for the structured part of a log line. */
export function formatEventContext(
  event: Partial<ValidatedAnalyticsEvent>,
  extra: Extra = {},
): string {
  return [
    event.ctaType && `${LOG_KEYS.ctaType}=${event.ctaType}`,
    event.target && `${LOG_KEYS.target}=${event.target}`,
    event.formId && `${LOG_KEYS.formId}=${event.formId}`,
    event.formStatus && `${LOG_KEYS.formStatus}=${event.formStatus}`,
    event.detail && `${LOG_KEYS.detail}=${event.detail}`,
    event.sectionVersion && `${LOG_KEYS.sectionVersion}=${event.sectionVersion}`,
    typeof extra.documentY === "number" && `${LOG_KEYS.documentY}=${extra.documentY}`,
    event.utm &&
      Object.entries(event.utm)
        .filter(([, v]) => Boolean(v))
        .map(([k, v]) => `${k}=${v}`)
        .join(" "),
  ]
    .filter(Boolean)
    .join(" ");
}

/** One canonical log line, identical in the browser and on the server. */
export function formatEventLog(event: Partial<ValidatedAnalyticsEvent>, extra: Extra = {}): string {
  const context = formatEventContext(event, extra);
  return `${LOG_PREFIX} ${event.page} ${event.name} "${event.label}"${context ? ` ${context}` : ""}`;
}

/** Error line for payloads that failed validation or plausibility checks. */
export function formatEventError(
  event: Partial<ValidatedAnalyticsEvent>,
  reason: string,
  what = "invalid event",
): string {
  return `${LOG_PREFIX} ${what} on ${event.page} ${event.name} "${event.label}": ${reason}`;
}

export type ConversionEventRow = {
  name: string;
  label: string;
  page: string;
  target: string | null;
  cta_type: string | null;
  form_id: string | null;
  form_status: string | null;
  detail: string | null;
  utm: Record<string, string | undefined> | null;
};

/** camelCase payload -> snake_case database row. */
export function toConversionRow(event: {
  name: string;
  label: string;
  page: string;
  target?: string | undefined;
  ctaType?: string | undefined;
  formId?: string | undefined;
  formStatus?: string | undefined;
  detail?: string | undefined;
  utm?: Record<string, string | undefined> | undefined;
}): ConversionEventRow {
  return {
    [COLUMN_NAMES.name]: event.name,
    [COLUMN_NAMES.label]: event.label,
    [COLUMN_NAMES.page]: event.page,
    [COLUMN_NAMES.target]: event.target ?? null,
    [COLUMN_NAMES.ctaType]: event.ctaType ?? null,
    [COLUMN_NAMES.formId]: event.formId ?? null,
    [COLUMN_NAMES.formStatus]: event.formStatus ?? null,
    [COLUMN_NAMES.detail]: event.detail ?? null,
    [COLUMN_NAMES.utm]: event.utm && Object.keys(event.utm).length > 0 ? event.utm : null,
  };
}
